import app from 'flarum/forum/app';
import {ApiPayloadPlural} from 'flarum/common/Store';
import extractText from 'flarum/common/utils/extractText';
import Picture from '../models/Picture';
import {License} from '../../common/json-settings';

export interface Upload {
    picture?: Picture
    uploading?: boolean
    saving?: boolean
    progress?: number
    error?: string
}

export default class Uploader {
    albumId: string = ''
    pictures: Upload[] = []
    loadingPictures: boolean = false
    license: string | null = null
    submitting: boolean = false
    hasExistingDrafts: boolean = false
    supportsSilentPublish: boolean = false
    publishSilently: boolean = false
    shouldReload: boolean = false

    constructor(albumId: string) {
        this.albumId = albumId;

        if (app.forum.attribute('cimaiseLicenseRequired')) {
            const licenses: License[] = app.forum.attribute('cimaiseLicenses') || [];

            if (licenses.length) {
                this.license = licenses[0].key;
            }
        }

    }

    protected loadExistingDraftsInternal(): Promise<Picture[]> {
        this.pictures = [];
        this.loadingPictures = true;

        return app.store.find<Picture[]>('cimaise/pictures', {
            filter: {
                q: 'album:' + this.albumId + ' is:draft',
            },
        }).then(pictures => {
            pictures.forEach(picture => {
                this.pictures.push({
                    picture,
                });
            });

            if (pictures.length) {
                this.hasExistingDrafts = true;
            }

            this.loadingPictures = false;
            this.shouldReload = false;

            return pictures;
        }).catch(err => {
            this.loadingPictures = false;
            this.shouldReload = true;
            throw err;
        });
    }

    loadExistingDraftsWithoutRedrawing(): Promise<Picture[]> {
        return this.loadExistingDraftsInternal();
    }

    loadExistingDrafts() {
        this.loadExistingDraftsInternal().then(() => {
            m.redraw();
        }).catch(err => {
            m.redraw();
            throw err;
        });
    }

    upload() {
        const $input = $('<input type="file">');
        $input.attr('multiple', 'true');
        $input.appendTo('body').hide().trigger('click').on('change', event => {
            const target = event.target as HTMLInputElement;

            if (target.files) {
                for (let i = 0; i < target.files.length; i++) {
                    this.uploadFile(target.files[i]);
                }
            }
        });
    }

    uploadFile(file: File) {
        const body = new FormData();

        body.append('image', file);

        const picture: Upload = {
            uploading: true,
            progress: 0,
        };
        this.pictures.push(picture);
        m.redraw();

        app.request<ApiPayloadPlural>({
            method: 'POST',
            url: app.forum.attribute('apiUrl') + '/cimaise/albums/' + this.albumId + '/upload',
            serialize: (raw: any) => raw,
            config: (xhr: XMLHttpRequest) => {
                xhr.upload.addEventListener('progress', event => {
                    picture.progress = Math.round(event.loaded / event.total * 100);

                    // We don't redraw on every event as that would be very expensive
                    // However we will redraw for 100% because the progress bars will change from "uploading" to "processing"
                    if (picture.progress === 100) {
                        m.redraw();
                    }
                });
            },
            body,
        }).then(result => {
            picture.uploading = false;
            app.store.pushPayload<Picture[]>(result).forEach(pictureRecord => {
                // There should always be only one picture, so we can override the attribute
                picture.picture = pictureRecord;
            });
            m.redraw();
        }).catch(err => {
            picture.uploading = false;
            if (err.alert && err.alert.content) {
                picture.error = extractText(err.alert.content);
            } else {
                // This code should not even run when it's an HTTP error since Flarum always prepares an error alert with a message
                // But it could happen due to other javascript errors
                picture.error = err + '';
            }
            m.redraw();
            this.shouldReload = true;
            throw err;
        });
    }

    canSubmitAll() {
        return this.pictures.every(picture => picture.picture);
    }

    async submitAll() {
        this.submitting = true;

        // Run requests in sequence
        for (let i = 0; i < this.pictures.length; i++) {
            const picture = this.pictures[i];

            // Skip pictures that failed to upload or were already submitted during a previous attempt
            if (!picture.picture || !picture.picture.isDraft) {
                continue;
            }

            try {
                await picture.picture.save({
                    isDraft: false,
                    withoutEventPost: this.publishSilently,
                });
            } catch (e) {
                this.submitting = false;
                this.shouldReload = true;
                m.redraw();

                throw e;
            }
        }

        this.submitting = false;
        m.redraw();
    }

    shouldChooseLicense(): boolean {
        return (app.forum.attribute<[]>('cimaiseLicenses') || []).length > 0;
    }

    setLicenseForAll(license: string): Promise<void | void[]> {
        this.license = license;

        if (license === null) {
            return Promise.resolve();
        }

        return Promise.all(this.pictures.map(picture => {
            if (!picture.picture) {
                return Promise.resolve();
            }

            if (picture.picture.license() === license) {
                return Promise.resolve();
            }

            return this.savePicture(picture.picture, {
                license,
            });
        }));
    }

    savePicture(picture: Picture, attributes: any): Promise<void> {
        const data = this.pictures.find(data => data.picture === picture);

        if (!data || !data.picture) {
            console.error('Uploader.savePicture: no data', picture);

            return Promise.reject();
        }

        data.saving = true;

        return data.picture.save(attributes).then(() => {
            data.saving = false;
            m.redraw();
        }).catch(e => {
            data.saving = false;
            m.redraw();
            throw e;
        });
    }

    deletePicture(picture: Picture): Promise<void> {
        const index = this.pictures.findIndex(data => data.picture === picture);
        const data = this.pictures[index];

        if (!data || !data.picture) {
            console.error('Uploader.deletePicture: no data', picture);

            return Promise.reject();
        }

        data.saving = true;

        return data.picture.delete().then(() => {
            this.pictures.splice(index, 1);
            m.redraw();
        }).catch(e => {
            this.pictures[index].saving = false;
            m.redraw();
            throw e;
        });
    }

    disabledPictureEdit(picture: Picture) {
        if (this.submitting) {
            return true;
        }

        return this.pictures.some(data => {
            if (data.picture !== picture) {
                return false;
            }

            return data.saving;
        });
    }

    disabledGlobalActions() {
        if (this.submitting) {
            return true;
        }

        return this.pictures.some(data => data.saving);
    }
}
